import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useRef } from 'react'
import type { MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ExperiencesPassportListModal from './ExperiencesPassportListModal'
import type { ExperiencesPassport, ExperiencesPassportInclusion } from './experiencesPassportTypes'
import { trackExperiencesPassportEvent } from 'analytics/snowplow/events/experiencesPassportEvents'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import ExperiencesPassportItemDetailsModal from './ExperiencesPassportItemDetailsModal'

const Root = styled.div`
  position: relative;
  margin-top: ${rem(32)};
  border: ${rem(1)} solid ${props => props.theme.palette.highlight.primary.darkForeground};
  padding: ${rem(32)} ${rem(16)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    margin-top: ${rem(24)};
  }

  &.variant-search-tile {
    margin-top: ${rem(12)};
    padding: ${rem(22)} ${rem(10)} ${rem(10)};
  }
`

const Label = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: ${rem(-1)};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(6)} ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.normalBackground};
  background-color: ${props => props.theme.palette.highlight.primary.darkForeground};
  border-radius: ${rem(2)};

  &.variant-search-tile {
    gap: ${rem(4)};
    padding: ${rem(3)} ${rem(6)};
  }
`

interface Props {
  passport: ExperiencesPassport
  primaryActionTo?: string
  variant?: 'offer-page' | 'search-tile'
}

function ExperiencesPassportSummaryBox({ passport, primaryActionTo, variant = 'offer-page' }: Props) {
  const showModal = useContext(ModalContext)
  const visibleInclusions = passport.inclusions.slice(0, 3)
  const hiddenCount = Math.max(passport.inclusions.length - visibleInclusions.length, 0)
  const isSearchTile = variant === 'search-tile'
  const variantClassName = `variant-${variant}`
  const trackingSurface = isSearchTile ? 'search_tile' : 'offer_page'
  const hasTrackedImpression = useRef(false)

  useEffect(() => {
    if (!hasTrackedImpression.current) {
      trackExperiencesPassportEvent('impression', trackingSurface)
      hasTrackedImpression.current = true
    }
  }, [trackingSurface])

  const openItemDetails = useCallback((inclusion: ExperiencesPassportInclusion) => {
    trackExperiencesPassportEvent('item_opened', trackingSurface)
    showModal(<ExperiencesPassportItemDetailsModal inclusion={inclusion} hideBackButton />)
  }, [showModal, trackingSurface])

  const openListModal = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (visibleInclusions.length === 1) {
      openItemDetails(visibleInclusions[0])
      return
    }

    trackExperiencesPassportEvent('list_opened', trackingSurface)
    showModal(<ExperiencesPassportListModal passport={passport} primaryActionTo={primaryActionTo} variant={variant} />)
  }, [passport, primaryActionTo, showModal, trackingSurface, variant, openItemDetails, visibleInclusions])

  return <Root className={variantClassName}>
    <Label className={variantClassName}>
      <LineGiftIcon size={isSearchTile ? 'XS' : 'S'} />
      <BodyText variant={isSearchTile ? 'small' : 'medium'}>
        <FormattedMessage
          id="LXDKe8ZUXOmH0PlTDrIn"
          defaultMessage="Exclusive Extras"
          description='Title for the "Experiences Passport" modal and promotional label'
        />
      </BodyText>
    </Label>
    <Group direction="vertical" gap={isSearchTile ? 4 : 8}>
      <BodyText variant={isSearchTile ? 'small' : 'medium'} wrap="word-wrap">
        {passport.totalValue ? <FormattedMessage
          id="VYSlKTIMSM-tBew5od0S"
          defaultMessage="Enjoy exclusive {destinationName} experiences with your stay, <valueHighlight>valued at over {value}</valueHighlight>."
          description="Experiences Passport summary copy with total value"
          values={{
            destinationName: passport.destinationName,
            value: <FormatCurrency
              value={passport.totalValue}
              currency={passport.totalValueCurrencyCode}
              format="roundedDollar"
            />,
            valueHighlight: (chunks) => <BodyText as="span" variant={isSearchTile ? 'small' : 'medium'} colour="highlight-primary-dark" weight="bold">{chunks}</BodyText>,
          }}
        /> : <FormattedMessage
          id="A5eMvdDIBwNz_RU4Q6Mx"
          defaultMessage="Enjoy exclusive {destinationName} experiences with your stay."
          description="Experiences Passport summary copy without total value"
          values={{ destinationName: passport.destinationName }}
        />}
      </BodyText>
      <Group direction="vertical" gap={isSearchTile ? 2 : 4}>
        {visibleInclusions.map(inclusion => <OfferInclusionItem
          key={inclusion.id}
          inclusion={{
            id: inclusion.id,
            description: inclusion.title,
            symbol: inclusion.symbol,
          }}
          size={isSearchTile ? 'small' : 'medium'}
          iconSize={isSearchTile ? 'XS' : 'S'}
          lineClamp={1}
        />)}
      </Group>
      <Group direction="horizontal">
        <TextLink size={isSearchTile ? 'small' : 'medium'} onClick={openListModal}>
          {!!hiddenCount && <FormattedMessage
            id="fh0XRtsdupFf6j0zHFge"
            defaultMessage="+ {count} more experiences"
            description="Link text to show additional hidden Experiences Passport inclusions"
            values={{ count: hiddenCount }}
          />}
          {!hiddenCount && <FormattedMessage
            id="AaZue59UtbiOpeJ1dApu"
            defaultMessage="Learn more"
            description="Link text to show Experiences Passport details modal"
          />}
        </TextLink>
      </Group>
    </Group>
  </Root>
}

export default ExperiencesPassportSummaryBox
